import React, { useEffect, useMemo, useState } from "react";

import find from "lodash/find";
import get from "lodash/get";
import map from "lodash/map";
import clsx from "clsx";
import dayjs from "dayjs";
import * as yup from "yup";
import Select from "react-select";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useParams, useHistory, Link } from "react-router-dom";

import DatePicker from "react-datepicker";

import {
  UploadInput,
  UploadPreview,
  useFiles,
  useUploadProvider,
} from "../../../../../components/FileUpload";

import {
  MUTATION_CREATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
  MUTATION_UPDATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
} from "../../../../../config/graphql/mutation";

import {
  QUERY_DIGITAL_RECEPTION,
  QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
  QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
  QUERY_DIGITAL_RECEPTION_MANUAL_REGISTRATION,
  QUERY_DIGITAL_RECEPTION_SETTINGS,
} from "../../../../../config/graphql/query";

import { reactSelectCustomStyles } from "../../../../Employees/Employee/Information";
import { POST_CHECKIN_FORM_RECEPTIONS } from "../../../../../config/const/common";

// Mirrors DEPENDS_ON_FIELD_TYPES on the server: only these offer something to compare against.
const DEPENDS_ON_FIELD_TYPES: ManualRegistrationFieldType[] = [
  "Dropdown",
  "Checkbox",
  "RadioButtons",
];

const CHECKBOX_DEPENDS_ON_VALUE = "true";

interface FieldValues {
  title?: string;
  text?: string;
  step: EntranceAgreementStep;
  // Flat here, one subdocument on the server -- the same way a question carries its own rule.
  dependsOnField?: string;
  dependsOnValue?: string;
  disabled: boolean;
  approvalRequired: boolean;
  audience: IEntranceAgreementAudience;
  validFrom?: Date;
  validUntil?: Date;
}

const AUDIENCE_MEMBERS: EntranceAgreementAudienceMember[] = [
  "employee",
  "visitor",
  "guest",
  "meetingAttendee",
];

const EntranceAgreementFile = React.memo(() => {
  const history = useHistory();

  const { t } = useTranslation(["digitalReceptionEntranceAgreement", "common"]);

  const { screenId, agreementId, agreementFileId } = useParams<{
    screenId?: string;
    agreementId?: string;
    agreementFileId?: string;
  }>();

  const [files, { onUpload: onUploadDocument }] = useFiles("Document");

  const schema = useMemo(() => {
    return yup.object().shape({
      title: yup.string(),
      text: yup.string(),
      disabled: yup.boolean().required(),
      approvalRequired: yup.boolean().required(),
      validFrom: yup
        .date()
        .transform((v, o) => (o === null ? undefined : v))
        .notRequired(),
      validUntil: yup
        .mixed()
        .transform((v, o) => (o === null ? undefined : v))
        .test(
          "allow",
          t(
            "digitalReceptionEntranceAgreement:entranceAgreementFile.yup.invalid",
          ),
          function validate(value) {
            if (!(value instanceof Date)) {
              return true;
            }

            if (!this.parent.validFrom) {
              return true;
            }

            return dayjs(value).isAfter(dayjs(this.parent.validFrom));
          },
        ),
    });
  }, [t]);

  const [language, setLanguage] = useState<string>();

  const { data: screenData } = useQuery<{
    digitalReception: IDigitalReception;
  }>(QUERY_DIGITAL_RECEPTION, {
    skip: !screenId,
    variables: {
      id: screenId,
    },
    onCompleted: ({ digitalReception: { languages, language: lng } }) => {
      setLanguage(language && languages.includes(language) ? language : lng);
    },
  });

  // Anywhere else a held-back document would leave the check-in and never be shown at all.
  const isPostCheckinFormReception = useMemo(
    () =>
      POST_CHECKIN_FORM_RECEPTIONS.includes(
        screenData?.digitalReception?.template || "",
      ),
    [screenData],
  );

  const stepOptions = useMemo(
    () =>
      (["checkin", "afterQuestions"] as EntranceAgreementStep[]).map(
        (value) => ({
          value,
          label: t(
            `digitalReceptionEntranceAgreement:entranceAgreementFile.form.steps.${value}`,
          ),
        }),
      ),
    [t],
  );

  // The rule is built from the set this reception asks -- the same one the server validates against.
  const { data: settingsData } = useQuery<{
    digitalReception: IDigitalReception;
  }>(QUERY_DIGITAL_RECEPTION_SETTINGS, {
    skip: !screenId || !isPostCheckinFormReception,
    variables: { id: screenId },
  });

  const postCheckinFormRegistrationId =
    settingsData?.digitalReception?.settings?.layout?.postCheckinForm
      ?.registration?.id;

  const { data: postCheckinFormData } = useQuery<{
    manualRegistration: IManualRegistration;
  }>(QUERY_DIGITAL_RECEPTION_MANUAL_REGISTRATION, {
    skip: !postCheckinFormRegistrationId || !language,
    // The questions a document can hang off, in the language being edited.
    variables: { id: postCheckinFormRegistrationId, language },
    fetchPolicy: "cache-and-network",
  });

  const languageOptions = useMemo(
    () =>
      map(screenData?.digitalReception.languages, (value) => ({
        value,
        label: t(`languages:${value}`),
      })) ?? [],
    [screenData, t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    defaultValues: {
      title: "",
      text: "",
      step: "checkin",
      dependsOnField: undefined,
      dependsOnValue: undefined,
      disabled: false,
      approvalRequired: true,
      audience: {
        employee: { enabled: true },
        visitor: { enabled: true },
        guest: { enabled: true },
        meetingAttendee: { enabled: true },
      },
      validFrom: undefined,
      validUntil: undefined,
    },
  });

  const {
    data: entranceAgreementFileData,
    loading,
    refetch,
  } = useQuery<{
    entranceAgreementFile: IEntranceAgreementFile;
  }>(QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE, {
    variables: {
      id: agreementFileId,
    },
    // The language travels in a header, not a variable, so the cache holds one document whatever
    // language filled it last: never read from it, and never ask before a language is picked.
    skip: !agreementFileId || !language,
    fetchPolicy: "network-only",
    onError: () => {
      history.replace(
        `/digital-receptions/${screenId}/settings/entrance-agreements/${agreementId}`,
      );
    },
    context: {
      headers: {
        "Accept-Language": language,
      },
    },
  });

  useEffect(() => {
    if (agreementFileId && language) {
      refetch({ id: agreementFileId });
    }
  }, [language, refetch, agreementFileId]);

  // A file picked but not saved yet belongs to the language it was picked in; carried over, it
  // would stand in for the new language's own file and be saved under it.
  const { onChange: setUploads } = useUploadProvider();

  useEffect(() => {
    setUploads({ Document: [] });
  }, [language, setUploads]);

  useEffect(() => {
    if (entranceAgreementFileData?.entranceAgreementFile) {
      const {
        entranceAgreementFile: { validFrom, validUntil, dependsOn, ...rest },
      } = entranceAgreementFileData;

      methods.reset({
        ...rest,
        dependsOnField: dependsOn?.field,
        dependsOnValue: dependsOn?.value,
        validFrom: validFrom ? new Date(validFrom) : undefined,
        validUntil: validUntil ? new Date(validUntil) : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entranceAgreementFileData]);

  const [onUpdate] = useMutation(
    MUTATION_UPDATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
    {
      refetchQueries: [
        {
          query: QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
          variables: { id: agreementFileId },
        },
      ],
      context: {
        headers: {
          "Accept-Language": language,
        },
      },
    },
  );
  const [onCreate] = useMutation(
    MUTATION_CREATE_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT_FILE,
    {
      refetchQueries: [
        {
          query: QUERY_DIGITAL_RECEPTION_ENTRANCE_AGREEMENT,
          variables: { id: screenId },
        },
      ],
      context: {
        headers: {
          "Accept-Language": language,
        },
      },
    },
  );

  const selectedStep = methods.watch("step");
  const dependsOnField = methods.watch("dependsOnField");

  // A rule only has answers to read after the questions; on a check-in document it would do nothing.
  const isConditionalStep =
    isPostCheckinFormReception && selectedStep === "afterQuestions";

  // Only a question that offers a choice can be depended on, and it may not be conditional itself --
  // the server rejects both, so neither is offered here.
  const dependsOnFieldOptions = useMemo(
    () =>
      (postCheckinFormData?.manualRegistration.fields ?? [])
        .filter(
          (field) =>
            DEPENDS_ON_FIELD_TYPES.includes(field.type) && !field.dependsOn,
        )
        .map((field) => ({ value: field.code, label: field.label })),
    [postCheckinFormData],
  );

  const dependsOnParent = useMemo(
    () =>
      (postCheckinFormData?.manualRegistration.fields ?? []).find(
        (field) => field.code === dependsOnField,
      ),
    [postCheckinFormData, dependsOnField],
  );

  const dependsOnValueOptions = useMemo(() => {
    if (!dependsOnParent) return [];

    if (dependsOnParent.type === "Checkbox") {
      return [
        {
          value: CHECKBOX_DEPENDS_ON_VALUE,
          label: t(
            "digitalReceptionEntranceAgreement:entranceAgreementFile.form.dependsOnChecked",
          ),
        },
      ];
    }

    const options =
      dependsOnParent.type === "Dropdown"
        ? dependsOnParent.dropdownOptions
        : dependsOnParent.radioButtons?.buttons;

    return (options ?? []).map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, [dependsOnParent, t]);

  const onSubmit = async (values: FieldValues) => {
    const {
      title,
      text,
      step,
      dependsOnField: dependsOnFieldValue,
      dependsOnValue,
      disabled,
      approvalRequired,
      audience,
      validFrom,
      validUntil,
    } = values;

    let input = {
      title,
      disabled,
      text,
      step,
      // Null clears the rule; a document no longer shown after the questions loses it anyway.
      dependsOn:
        step === "afterQuestions" && dependsOnFieldValue && dependsOnValue
          ? { field: dependsOnFieldValue, value: dependsOnValue }
          : null,
      approvalRequired,
      audience: {
        employee: { enabled: !!audience?.employee?.enabled },
        visitor: { enabled: !!audience?.visitor?.enabled },
        guest: { enabled: !!audience?.guest?.enabled },
        meetingAttendee: { enabled: !!audience?.meetingAttendee?.enabled },
      },
      validFrom: validFrom ?? null,
      validUntil: validUntil ?? null,
    };

    if (Array.isArray(files) && files.length > 0) {
      const uploadedFiles = await onUploadDocument();

      if (uploadedFiles && uploadedFiles[0]) {
        input = {
          ...input,
          // @ts-ignore
          file: uploadedFiles[0]?.file?.id,
        };
      }
    }

    if (!!agreementFileId) {
      return onUpdate({
        variables: {
          input: {
            id: agreementFileId,
            ...input,
          },
        },
      })
        .then(() => {
          toast.success<string>(
            t(
              "digitalReceptionEntranceAgreement:entranceAgreementFile.toast.updated",
            ),
          );

          history.replace(
            `/digital-receptions/${screenId}/settings/entrance-agreements/${agreementId}`,
          );
        })
        .catch((error) => {
          toast.error<string>(
            t(
              error?.networkError?.result?.errors?.[0]?.message ??
                error?.message,
            ),
          );
        });
    }

    return onCreate({
      variables: {
        input: {
          entranceAgreement: agreementId,
          ...input,
        },
      },
    })
      .then(() => {
        toast.success<string>(
          t(
            "digitalReceptionEntranceAgreement:entranceAgreementFile.toast.created",
          ),
        );

        history.replace(
          `/digital-receptions/${screenId}/settings/entrance-agreements/${agreementId}`,
        );
      })
      .catch((error) => {
        toast.error<string>(
          t(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          ),
        );
      });
  };

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/digital-receptions">
              {t("screens:screensRouter.nav.screens")}
            </Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/digital-receptions/${screenId}`}>
              {t("screens:screen.screenRoute.nav.screen")}
            </Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/digital-receptions/${screenId}/settings`}>
              {t("screens:screen.screenRoute.nav.settings")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <Link
              to={`/digital-receptions/${screenId}/settings/entrance-agreements/${agreementId}`}
            >
              {t("screens:screen.screenRoute.nav.entranceAgreement")}
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("screens:screen.screenRoute.nav.entranceAgreementFile")}
          </li>
        </ol>
      </nav>
      <div className="row mb-4">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <Select
            options={languageOptions}
            value={languageOptions.find((option) => option.value === language)}
            onChange={(value) => {
              setLanguage(value?.value);
            }}
            styles={reactSelectCustomStyles(false)}
          />
        </div>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-lg-4 col-md-6 col-sm-12">
              <div className="form-group">
                <label>
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFile.form.audience.label",
                  )}
                </label>
                {AUDIENCE_MEMBERS.map((member) => (
                  <Controller
                    key={member}
                    name={`audience.${member}.enabled` as const}
                    render={({ field: { value, name, onChange, ...rest } }) => (
                      <div className="form-check">
                        <input
                          id={name}
                          {...rest}
                          type="checkbox"
                          className="form-check-input"
                          checked={value === true}
                          onChange={() => onChange(!value)}
                          disabled={methods.watch("disabled")}
                        />
                        <label className="form-check-label" htmlFor={name}>
                          {t(
                            `digitalReceptionEntranceAgreement:entranceAgreementFile.form.audience.${member}`,
                          )}
                        </label>
                      </div>
                    )}
                  />
                ))}
                <small className="form-text text-muted small">
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFile.form.audience.helper",
                  )}
                </small>
              </div>
              {isPostCheckinFormReception && (
                <div className="form-group">
                  <label htmlFor="step">
                    {t(
                      "digitalReceptionEntranceAgreement:entranceAgreementFile.form.step.label",
                    )}
                  </label>
                  <Controller
                    name="step"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        inputId="step"
                        options={stepOptions}
                        value={
                          stepOptions.find(
                            (option) => option.value === value,
                          ) ?? stepOptions[0]
                        }
                        isSearchable={false}
                        isDisabled={methods.watch("disabled")}
                        styles={reactSelectCustomStyles(false)}
                        onChange={(next) => onChange(next?.value ?? "checkin")}
                      />
                    )}
                  />
                  <small className="form-text text-muted small">
                    {t(
                      "digitalReceptionEntranceAgreement:entranceAgreementFile.form.step.helper",
                    )}
                  </small>
                </div>
              )}
              {/* Before the questions nothing has been answered, so the rule would do nothing. */}
              {isConditionalStep && dependsOnFieldOptions.length > 0 && (
                <div className="form-group border rounded p-3 my-3">
                  <label>
                    {t(
                      "digitalReceptionEntranceAgreement:entranceAgreementFile.form.dependsOn",
                    )}
                  </label>
                  <small className="form-text text-muted mb-2">
                    {t(
                      "digitalReceptionEntranceAgreement:entranceAgreementFile.form.dependsOnHelp",
                    )}
                  </small>
                  <Controller
                    name="dependsOnField"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        options={dependsOnFieldOptions}
                        value={find(dependsOnFieldOptions, { value }) ?? null}
                        isClearable
                        placeholder={t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFile.form.dependsOnAlways",
                        )}
                        isDisabled={methods.watch("disabled")}
                        styles={reactSelectCustomStyles(false)}
                        onChange={(nextValue) => {
                          onChange(get(nextValue, "value"));

                          // The old answer belongs to the old question, so it never carries over.
                          methods.setValue("dependsOnValue", undefined);
                        }}
                      />
                    )}
                  />
                  {!!dependsOnField && (
                    <Controller
                      name="dependsOnValue"
                      render={({ field: { onChange, value } }) => (
                        <div className="mt-2">
                          <Select
                            options={dependsOnValueOptions}
                            value={
                              find(dependsOnValueOptions, { value }) ?? null
                            }
                            placeholder={t(
                              "digitalReceptionEntranceAgreement:entranceAgreementFile.form.dependsOnValue",
                            )}
                            isDisabled={methods.watch("disabled")}
                            styles={reactSelectCustomStyles(false)}
                            onChange={(nextValue) =>
                              onChange(get(nextValue, "value"))
                            }
                          />
                        </div>
                      )}
                    />
                  )}
                </div>
              )}
              <Controller
                name="approvalRequired"
                render={({ field: { value, name, onChange, ...rest } }) => {
                  return (
                    <div className="form-check my-3">
                      <input
                        id={name}
                        {...rest}
                        type="checkbox"
                        className="form-check-input"
                        checked={value === true}
                        onChange={() => onChange(!value)}
                        disabled={methods.watch("disabled")}
                      />
                      <label className="form-check-label" htmlFor={name}>
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFile.form.approvalRequired",
                        )}
                      </label>
                    </div>
                  );
                }}
              />
              <div>
                <label htmlFor="validFrom">
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFile.form.validFrom",
                  )}
                </label>
                <div className="mb-2">
                  <Controller
                    name="validFrom"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <>
                        <div
                          className={clsx("", {
                            "is-invalid": !!error,
                          })}
                        >
                          <DatePicker
                            className={clsx("form-control", {
                              "is-invalid": !!error,
                            })}
                            // showTimeSelect
                            selected={value}
                            onChange={onChange}
                            // filterTime={(time) => dayjs() <= dayjs(time)}
                            // timeIntervals={30}
                            // dateFormat="dd/MM/yyyy p"
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            shouldCloseOnSelect
                            disabled={methods.watch("disabled")}
                          />
                        </div>

                        <div className="invalid-feedback">
                          {error?.message ?? "Invalid"}
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="validUntil">
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFile.form.validUntil",
                  )}
                </label>
                <div>
                  <Controller
                    name="validUntil"
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <>
                        <div
                          className={clsx("mb3", {
                            "is-invalid": !!error,
                          })}
                        >
                          <DatePicker
                            className={clsx("form-control", {
                              "is-invalid": !!error,
                            })}
                            // showTimeSelect
                            selected={value}
                            onChange={onChange}
                            minDate={methods.watch("validFrom")}
                            // filterTime={(time) =>
                            //   dayjs(methods.getValues("validFrom")) < dayjs(time)
                            // }
                            // timeIntervals={30}
                            // dateFormat="dd/MM/yyyy p"
                            dateFormat="dd/MM/yyyy"
                            isClearable
                            shouldCloseOnSelect
                            disabled={methods.watch("disabled")}
                          />
                        </div>

                        <div className="invalid-feedback">
                          {error?.message ?? "Invalid"}
                        </div>
                      </>
                    )}
                  />
                </div>
              </div>
              <Controller
                name="disabled"
                render={({ field: { value, name, onChange, ...rest } }) => {
                  return (
                    <div className="form-check my-3">
                      <input
                        id={name}
                        {...rest}
                        type="checkbox"
                        className="form-check-input"
                        checked={value === true}
                        onChange={() => onChange(!value)}
                      />
                      <label className="form-check-label" htmlFor={name}>
                        {t(
                          "digitalReceptionEntranceAgreement:entranceAgreementFile.form.disabled",
                        )}
                      </label>
                    </div>
                  );
                }}
              />
              <button
                type="submit"
                className="btn btn-primary mt-3"
                disabled={loading}
              >
                {t(
                  `digitalReceptionEntranceAgreement:entranceAgreementFile.form.button.${
                    !agreementFileId ? "create" : "update"
                  }`,
                )}
              </button>
            </div>
            <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
              <div className="form-group">
                <label htmlFor="file">
                  {t(
                    "digitalReceptionEntranceAgreement:entranceAgreementFile.form.document",
                  )}
                </label>
                <UploadInput
                  name="Document"
                  options={{
                    accept: {
                      "application/pdf": [".pdf"],
                      "image/png": [".png"],
                      "image/jpeg": [".jpg", ".jpeg"],
                    },
                    multiple: false,
                  }}
                />
                <UploadPreview
                  name="Document"
                  placeholder={
                    entranceAgreementFileData?.entranceAgreementFile?.file
                      ?.absolutePath
                  }
                  isPdf={
                    !!entranceAgreementFileData?.entranceAgreementFile?.file?.absolutePath.endsWith(
                      ".pdf",
                    )
                  }
                />
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
});

export default EntranceAgreementFile;
